import { Row } from 'components/Layout';
import TransactionAllocator from 'components/TransactionAllocator';
import { type Transaction } from 'components/TransactionAllocator';
import { useLayoutContext } from 'contexts/LayoutContext';
import { ActionTransactionSubTypes } from 'further-types/lp/action';
import { CurrencyRateDetails } from 'further-types/lp/transaction';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { numberToCurrencyString } from 'further-ui/utils';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api, { ApiResponses } from 'lib/trpcClient';
import { useEffect, useState } from 'react';
import CashHistory from './CashHistory';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { styled } from '@mui/material/styles';
import { Column } from 'further-ui/components/Layout';

const ExpandedRowContainer = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  borderTop: `1px solid ${theme.palette.grey[300]}`,
}));

type Props = {
  vehicleId: string;
  currencyRateOverrides: Map<CurrencyCode, CurrencyRateDetails>;
  data: ApiResponses['unrealisedCashFxGainOrLoss']['list'] | undefined;
  isFetching: boolean;
  onCurrenciesLoaded: (
    currencies: Array<[CurrencyCode, CurrencyRateDetails]>,
  ) => void;
  onApproveSuccess?: () => void;
  approveRef: React.MutableRefObject<(() => Promise<void>) | null>;
  onViewRecord?: (recordId: string) => void;
};

const Cash: React.FC<Props> = ({
  vehicleId,
  currencyRateOverrides,
  data,
  isFetching,
  onCurrenciesLoaded,
  onApproveSuccess,
  approveRef,
  onViewRecord,
}) => {
  const { vehicle } = useLayoutContext();
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);

  const [transactionChanges, setTransactionChanges] = useState<
    Map<string, Array<Transaction>>
  >(new Map());

  const { transactionTypeOptions, isLoading: transactionTypesLoading } =
    useTransactionTypeSelectorOptions();

  useEffect(() => {
    if (!data?.currencyRates) return;
    onCurrenciesLoaded(data.currencyRates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.currencyRates]);

  useEffect(() => {
    setTransactionChanges(new Map());
  }, [currencyRateOverrides]);

  const mutationOpts = useHandleTrpcMutation();
  const { mutateAsync: createUnrealisedCashFXGains } =
    api.unrealisedCashFxGainOrLoss.create.useMutation(
      mutationOpts({
        successMessage: 'Created unrealised cash FX gains/losses successfully.',
      }),
    );

  const postUnrealisedFXGains = async () => {
    const postUnrealisedFXGainsBody = data?.results.map((row) => {
      const transactions = transactionChanges.get(row.id);
      return {
        bankAccountId: row.id,
        transactions: (transactions ?? row.transactions).map((transaction) => ({
          transactionTypeId: transaction.transactionTypeId,
          operationType: transaction.operationType,
        })),
      };
    });

    await createUnrealisedCashFXGains({
      vehicleId,
      bankAccounts: postUnrealisedFXGainsBody ?? [],
      currencyRateOverrides: Object.fromEntries(currencyRateOverrides),
    });

    onApproveSuccess?.();
  };

  approveRef.current = postUnrealisedFXGains;

  const handleTransactionChange = (
    rowId: string,
    newTransactions: Array<Transaction>,
  ) => {
    setTransactionChanges((prev) => {
      const newMap = new Map(prev);
      newMap.set(rowId, newTransactions);
      return newMap;
    });
  };

  if (!vehicle) {
    return null;
  }

  const rows =
    data?.results?.map((row) => ({
      ...row,
      baseCurrency: vehicle.baseCurrency,
    })) ?? [];

  const buildAllocatorTransactions = (row: (typeof rows)[number]) => {
    const transactions = transactionChanges.get(row.id);

    if (transactions) {
      return transactions;
    }

    return row.transactions.map((transaction) => ({
      ...transaction,
      transactionCurrency: row.currencyCode,
      transactionSubType: ActionTransactionSubTypes.CreditDebitPair,
    }));
  };

  const columns: Array<TableColumn<(typeof rows)[number]>> = [
    {
      key: 'bankName',
      label: 'Bank name',
      disableSorting: true,
    },
    {
      key: 'accountName',
      label: 'Account name',
      disableSorting: true,
    },
    {
      key: 'balanceTcy',
      label: 'Balance (TCY)',
      render: (row) =>
        numberToCurrencyString(row.balanceTcy, {
          fallback: '-',
          currency: row.currencyCode,
        }),
      disableSorting: true,
    },
    {
      key: 'originalValueLcy',
      label: 'Original value (LCY)',
      render: (row) =>
        numberToCurrencyString(row.calculationBreakdown?.originalValueLcy, {
          fallback: '-',
          currency: row.baseCurrency,
        }),
      disableSorting: true,
    },
    {
      key: 'runningTotalUnrealisedLcy',
      label: 'Running total unrealised',
      render: (row) =>
        numberToCurrencyString(
          row.calculationBreakdown?.runningTotalUnrealisedLcy,
          {
            fallback: '-',
            currency: row.baseCurrency,
            negativeInBrackets: true,
          },
        ),
      disableSorting: true,
    },
    {
      key: 'currentValueLcy',
      label: 'Current value (LCY)',
      render: (row) =>
        numberToCurrencyString(row.calculationBreakdown?.currentValueLcy, {
          fallback: '-',
          currency: row.baseCurrency,
        }),
      disableSorting: true,
    },
    {
      key: 'unrealisedCashFxGainValueLcy',
      label: 'Unrealised gain/loss (LCY)',
      render: (row) =>
        numberToCurrencyString(row.unrealisedCashFxGainValueLcy, {
          fallback: '-',
          currency: row.baseCurrency,
          negativeInBrackets: true,
        }),
      disableSorting: true,
    },
  ];

  return (
    <Column spacing="lg">
      <Table
        columns={columns}
        rows={rows}
        isLoading={isFetching}
        isExpandableRow={() => true}
        isExpandedRow={(row) => expandedRowId === row.id}
        onRowExpand={(row) =>
          setExpandedRowId((prev) => (prev === row.id ? null : row.id))
        }
        getExpandedContent={(row) => (
          <ExpandedRowContainer>
            <Row fullWidth>
              {!transactionTypesLoading && (
                <TransactionAllocator
                  transactionTypeOptions={transactionTypeOptions}
                  transactions={buildAllocatorTransactions(row)}
                  onChange={(newTransactions) => {
                    handleTransactionChange(row.id ?? '', newTransactions);
                  }}
                  transactionCurrency={row.currencyCode}
                  vehicle={vehicle}
                  fxRate={row.currencyRate}
                />
              )}
            </Row>
          </ExpandedRowContainer>
        )}
      />
      <CashHistory vehicleId={vehicleId} onViewRecord={onViewRecord} />
    </Column>
  );
};

export default Cash;
